import React, { useState } from "react";
import {
  Modal,
  Button,
  Space,
  Typography,
  Checkbox,
  message,
  Card,
  Drawer,
  Tooltip,
} from "antd";
import {
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuth0 } from "@auth0/auth0-react";
import apiClient from "../utils/axios";
import { loadTermsAndConditions, loadPrivacyPolicy, LEGAL_DOCUMENTS } from "../utils/legal-documents";
import { generateTextPDF } from "../utils/pdf-generator";

const { Title, Text } = Typography;

// Add CSS animation for highlighting checkboxes
const pulseAnimation = `
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 169, 64, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(255, 169, 64, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 169, 64, 0);
    }
  }
`;

// Inject the CSS animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseAnimation;
  document.head.appendChild(style);
}

interface UserLegalAcceptanceModalProps {
  open: boolean;
  onSuccess: () => void;
}

interface DocumentViewerProps {
  title: string;
  content: string;
  open: boolean;
  onClose: () => void;
  onViewed: () => void;
  loading?: boolean;
  documentId: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  title,
  content,
  open,
  onClose,
  onViewed,
  loading = false,
  documentId,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadingText, setDownloadingText] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const element = document.getElementById(`user-document-content-${documentId}`);
      if (!element) {
        throw new Error('Document content element not found');
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Failed to generate canvas from content');
      }

      const imgData = canvas.toDataURL('image/png', 0.95);
      
      if (!imgData || imgData === 'data:,') {
        throw new Error('Failed to generate image data from canvas');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${title.replace(/\s+/g, '_')}.pdf`);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF with html2canvas:', error);
      
      try {
        console.log('Attempting fallback PDF generation...');
        generateTextPDF(title, content);
        message.success('PDF downloaded successfully (text format)');
      } catch (fallbackError) {
        console.error('Fallback PDF generation also failed:', fallbackError);
        message.error('Failed to generate PDF');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadTextPDF = async () => {
    setDownloadingText(true);
    try {
      generateTextPDF(title, content);
      message.success('Text-based PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating text PDF:', error);
      message.error('Failed to generate text-based PDF');
    } finally {
      setDownloadingText(false);
    }
  };

  const handleClose = () => {
    onViewed();
    onClose();
  };

  return (
    <Drawer
      title={
        <Space>
          <FileTextOutlined />
          {title}
        </Space>
      }
      open={open}
      onClose={handleClose}
      width="80%"
      extra={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
            disabled={loading || !content}
          >
            Download PDF
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={handleDownloadTextPDF}
            loading={downloadingText}
            disabled={loading || !content}
          >
            Text PDF
          </Button>
          <Button
            type="primary"
            onClick={handleClose}
            size="large"
          >
            Close
          </Button>
        </Space>
      }
    >
      <div
        id={`user-document-content-${documentId}`}
        style={{
          padding: '24px',
          backgroundColor: '#fff',
          lineHeight: '1.6',
          fontSize: '14px',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>Loading document...</Text>
          </div>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <Title level={1} style={{ color: '#1890ff', marginTop: '32px', marginBottom: '16px' }}>
                  {children}
                </Title>
              ),
              h2: ({ children }) => (
                <Title level={2} style={{ color: '#1890ff', marginTop: '24px', marginBottom: '12px' }}>
                  {children}
                </Title>
              ),
              h3: ({ children }) => (
                <Title level={3} style={{ marginTop: '20px', marginBottom: '8px' }}>
                  {children}
                </Title>
              ),
              p: ({ children }) => (
                <Text style={{ display: 'block', marginBottom: '12px' }}>
                  {children}
                </Text>
              ),
              ul: ({ children }) => (
                <ul style={{ marginBottom: '12px', paddingLeft: '20px' }}>
                  {children}
                </ul>
              ),
              li: ({ children }) => (
                <li style={{ marginBottom: '4px' }}>
                  {children}
                </li>
              ),
              strong: ({ children }) => (
                <Text strong>{children}</Text>
              ),
              em: ({ children }) => (
                <Text italic>{children}</Text>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
    </Drawer>
  );
};

export const UserLegalAcceptanceModal: React.FC<UserLegalAcceptanceModalProps> = ({
  open,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [accepting, setAccepting] = useState(false);
  
  // Document viewing state
  const [termsViewed, setTermsViewed] = useState(false);
  const [privacyViewed, setPrivacyViewed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  
  // Document content state
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);

  const canAccept = termsViewed && privacyViewed && termsAccepted && privacyAccepted;

  // Load terms content when needed
  const handleShowTerms = async () => {
    if (!termsContent) {
      setLoadingTerms(true);
      try {
        const content = await loadTermsAndConditions();
        setTermsContent(content);
      } catch (error) {
        message.error('Failed to load Terms and Conditions');
      } finally {
        setLoadingTerms(false);
      }
    }
    setShowTerms(true);
  };

  // Load privacy content when needed
  const handleShowPrivacy = async () => {
    if (!privacyContent) {
      setLoadingPrivacy(true);
      try {
        const content = await loadPrivacyPolicy();
        setPrivacyContent(content);
      } catch (error) {
        message.error('Failed to load Privacy Policy');
      } finally {
        setLoadingPrivacy(false);
      }
    }
    setShowPrivacy(true);
  };

  const handleAcceptLegalDocuments = async () => {
    if (!canAccept) {
      message.error("Please view and accept both legal documents");
      return;
    }

    try {
      setAccepting(true);
      
      const token = await getAccessTokenSilently();
      
      await apiClient.post("/api/user-legal/accept", {
        terms_version: LEGAL_DOCUMENTS.TERMS.version,
        privacy_version: LEGAL_DOCUMENTS.PRIVACY.version
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      message.success("Legal documents accepted successfully!");
      onSuccess();
    } catch (error: any) {
      console.error("Error accepting legal documents:", error);
      message.error(
        error.response?.data?.detail || "Failed to accept legal documents"
      );
    } finally {
      setAccepting(false);
    }
  };

  return (
    <>
      <Modal
        title="Accept Legal Documents"
        open={open}
        closable={false}
        maskClosable={false}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 24 }}>
          <Title level={4} style={{ marginBottom: 16 }}>Welcome to CodePilot!</Title>
          <Text style={{ display: 'block', marginBottom: 16 }}>
            Before you can access the application, please review and accept our legal documents:
          </Text>

          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>Terms and Conditions</Text>
                  {termsViewed && <Text type="success" style={{ marginLeft: 8 }}>✓ Viewed</Text>}
                </div>
                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={handleShowTerms}
                    size="small"
                    loading={loadingTerms}
                  >
                    View
                  </Button>
                  <Tooltip 
                    title={!termsViewed ? "Please view the document first by clicking the 'View' button" : ""}
                    placement="top"
                  >
                    <div style={{ 
                      padding: termsViewed && !termsAccepted ? '4px 8px' : '0',
                      borderRadius: '4px',
                      backgroundColor: termsViewed && !termsAccepted ? '#fff7e6' : 'transparent',
                      border: termsViewed && !termsAccepted ? '2px solid #ffa940' : 'none',
                      animation: termsViewed && !termsAccepted ? 'pulse 2s infinite' : 'none',
                    }}>
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        disabled={!termsViewed}
                      >
                        Accept
                      </Checkbox>
                    </div>
                  </Tooltip>
                </Space>
              </div>
            </Card>

            <Card size="small">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>Privacy Policy</Text>
                  {privacyViewed && <Text type="success" style={{ marginLeft: 8 }}>✓ Viewed</Text>}
                </div>
                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={handleShowPrivacy}
                    size="small"
                    loading={loadingPrivacy}
                  >
                    View
                  </Button>
                  <Tooltip 
                    title={!privacyViewed ? "Please view the document first by clicking the 'View' button" : ""}
                    placement="top"
                  >
                    <div style={{ 
                      padding: privacyViewed && !privacyAccepted ? '4px 8px' : '0',
                      borderRadius: '4px',
                      backgroundColor: privacyViewed && !privacyAccepted ? '#fff7e6' : 'transparent',
                      border: privacyViewed && !privacyAccepted ? '2px solid #ffa940' : 'none',
                      animation: privacyViewed && !privacyAccepted ? 'pulse 2s infinite' : 'none',
                    }}>
                      <Checkbox
                        checked={privacyAccepted}
                        onChange={(e) => setPrivacyAccepted(e.target.checked)}
                        disabled={!privacyViewed}
                      >
                        Accept
                      </Checkbox>
                    </div>
                  </Tooltip>
                </Space>
              </div>
            </Card>
          </Space>
        </div>
        
        <div style={{ textAlign: "center" }}>
          <Button
            type="primary"
            onClick={handleAcceptLegalDocuments}
            loading={accepting}
            disabled={!canAccept}
            size="large"
          >
            Accept and Continue
          </Button>
        </div>
      </Modal>

      <DocumentViewer
        title={LEGAL_DOCUMENTS.TERMS.title}
        content={termsContent}
        open={showTerms}
        onClose={() => setShowTerms(false)}
        onViewed={() => setTermsViewed(true)}
        loading={loadingTerms}
        documentId="terms"
      />

      <DocumentViewer
        title={LEGAL_DOCUMENTS.PRIVACY.title}
        content={privacyContent}
        open={showPrivacy}
        onClose={() => setShowPrivacy(false)}
        onViewed={() => setPrivacyViewed(true)}
        loading={loadingPrivacy}
        documentId="privacy"
      />
    </>
  );
}; 